/**
 * Keyboard capture and the typed-word buffer.
 *
 * The comparison logic at the top is pure and unit tested. The class below is a
 * thin Phaser wrapper that owns the keydown listener and stops the browser from
 * acting on the keys we need (CLAUDE.md: Space, Backspace, Tab, Enter, arrows).
 */
import type { Scene } from 'phaser';
import type { LetterState } from '../types';

/** Words are letters only, so anything else is ignored rather than typed. */
const PRINTABLE = /^[a-zA-Z]$/;
/** Sentence levels also take a full stop. */
const PRINTABLE_WITH_STOP = /^[a-zA-Z.]$/;

/**
 * How strictly typing is matched. Strict: exactly as written. Lenient (sentence
 * levels in grades 3 to 5): a capital may be typed in lowercase, and a final
 * full stop may be left off, since they are shown but not required there.
 */
export type Match = 'strict' | 'lenient';

/** Whether a typed character counts for the target character at the same place. */
export function charMatches(
  typed: string,
  target: string | undefined,
  match: Match = 'strict',
): boolean {
  if (target === undefined) return false;
  return match === 'lenient' ? typed.toLowerCase() === target.toLowerCase() : typed === target;
}

/** The part of a target that must be typed: a lenient final full stop is optional. */
export function requiredPart(target: string, match: Match = 'strict'): string {
  return match === 'lenient' && target.endsWith('.') ? target.slice(0, -1) : target;
}

/** Guard against a stuck key filling the buffer forever. */
export const MAX_TYPED_LENGTH = 24;

/** Keys the game consumes, so they never scroll or navigate the page. */
export const CAPTURED_KEYS = 'SPACE,BACKSPACE,TAB,ENTER,UP,DOWN,LEFT,RIGHT';

/**
 * Per-position state, long enough to cover whatever the player typed and the
 * whole target. Positions past the target are 'wrong' (overtyped), positions
 * the player has not reached yet are 'pending'.
 */
export function letterStates(
  typed: string,
  target: string,
  match: Match = 'strict',
): LetterState[] {
  const length = Math.max(typed.length, target.length);
  const states: LetterState[] = [];
  for (let i = 0; i < length; i += 1) {
    if (i >= typed.length) {
      states.push('pending');
      continue;
    }
    states.push(charMatches(typed[i] ?? '', target[i], match) ? 'correct' : 'wrong');
  }
  return states;
}

/** Exact match when strict: case matters, since grade 6+ words include proper nouns. */
export function isCorrect(typed: string, target: string, match: Match = 'strict'): boolean {
  if (match === 'strict') return typed === target;
  const lower = typed.toLowerCase();
  return lower === target.toLowerCase() || lower === requiredPart(target, match).toLowerCase();
}

/** Positions where the player typed the wrong letter, for the correction display. */
export function wrongPositions(typed: string, target: string, match: Match = 'strict'): number[] {
  const out: number[] = [];
  for (let i = 0; i < target.length; i += 1) {
    if (!charMatches(typed[i] ?? '', target[i], match)) out.push(i);
  }
  return out;
}

export interface TypingInputHandlers {
  /**
   * Optional say over each printable key before it is typed; false drops it.
   * Sentence levels use it to ignore a full stop typed after auto enter has
   * already sent the word it belonged to.
   */
  accept?(char: string, index: number): boolean;
  /** A letter was appended. `index` is its position in the word. */
  onCharacter(char: string, index: number): void;
  onBackspace(): void;
  onSubmit(typed: string): void;
}

export class TypingInput {
  private buffer = '';
  private active = true;
  private readonly listener: (event: KeyboardEvent) => void;
  /**
   * Phaser's KeyboardPlugin can replay a frame's queued events, which showed up
   * as "sea" being typed as "sseaa". keyboardOnce.ts now fixes that for every
   * scene; this stays as a second line of defence for the one place a doubled
   * key costs the player, and its count tells the harness if it ever returns.
   */
  private readonly seen = new WeakSet<KeyboardEvent>();
  private duplicatesSuppressed = 0;
  /** Sentence levels: the full stop is a typing key too. */
  allowFullStop = false;

  constructor(
    private readonly scene: Scene,
    private readonly handlers: TypingInputHandlers,
  ) {
    const keyboard = scene.input.keyboard;
    keyboard?.addCapture(CAPTURED_KEYS);

    this.listener = (event: KeyboardEvent): void => this.handleKey(event);
    keyboard?.on('keydown', this.listener);
  }

  /** While disabled, keystrokes are discarded rather than buffered (CLAUDE.md). */
  get enabled(): boolean {
    return this.active;
  }

  set enabled(value: boolean) {
    this.active = value;
  }

  get typed(): string {
    return this.buffer;
  }

  clear(): void {
    this.buffer = '';
  }

  destroy(): void {
    const keyboard = this.scene.input.keyboard;
    keyboard?.off('keydown', this.listener);
    keyboard?.removeCapture(CAPTURED_KEYS);
  }

  /** Duplicate dispatches ignored so far, surfaced for the dev harness. */
  get suppressedDuplicates(): number {
    return this.duplicatesSuppressed;
  }

  private handleKey(event: KeyboardEvent): void {
    if (this.seen.has(event)) {
      this.duplicatesSuppressed += 1;
      return;
    }
    this.seen.add(event);

    // Always swallow the game keys, even while locked, so a mistimed Space
    // cannot scroll the page mid-jump.
    if (
      event.key === ' ' ||
      event.key === 'Enter' ||
      event.key === 'Backspace' ||
      event.key === 'Tab' ||
      event.key.startsWith('Arrow')
    ) {
      event.preventDefault();
    }

    if (!this.active) return;
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    if (event.key === 'Backspace') {
      if (this.buffer.length > 0) {
        this.buffer = this.buffer.slice(0, -1);
        this.handlers.onBackspace();
      }
      return;
    }

    if (event.key === ' ' || event.key === 'Enter') {
      const submitted = this.buffer;
      if (submitted.length > 0) this.handlers.onSubmit(submitted);
      return;
    }

    const printable = this.allowFullStop ? PRINTABLE_WITH_STOP : PRINTABLE;
    if (printable.test(event.key) && this.buffer.length < MAX_TYPED_LENGTH) {
      const index = this.buffer.length;
      if (this.handlers.accept !== undefined && !this.handlers.accept(event.key, index)) return;
      this.buffer += event.key;
      this.handlers.onCharacter(event.key, index);
    }
  }
}
